import Vue from "vue";
import { reflectKeys, jwt, appLocalStorage } from '@/app/shared/services';

import { fetchGuide, fetchTags, fetchArtefacts, fetchArtefact, fetchArtefactUnit, fetchMock } from '../services';

/** Initial state */
const initialState = {
  loading: false,
  data: null,
  error: null,
  guide: [],
  tags: [],
  artefacts: [],
  artefact: {},
  artefactUnit: {}
};

/** Prefix for mutation types and actiontypes */
const namespacedPrefix = '[SANDBOX]';

/**
 * Mutation types
 */
const mutationTypes = reflectKeys(
  [
    'SANDBOX_DATA_REQUEST',
    'SANDBOX_DATA_SUCCESS',
    'SANDBOX_DATA_ERROR',
    'SANDBOX_DATA_RESET',
    'LOOKUP_SANDBOX_GUIDE',
    'SET_SANDBOX_GUIDE',
    'SET_SANDBOX_GUIDE_ON',
    'LOOKUP_SANDBOX_TAGS',
    'SET_SANDBOX_TAGS',
    'LOOKUP_SANDBOX_ARTEFACTS',
    'SET_SANDBOX_ARTEFACTS',
    'LOOKUP_SANDBOX_ARTEFACT',
    'SET_SANDBOX_ARTEFACT',
    'LOOKUP_SANDBOX_ARTEFACT_UNIT',
    'SET_SANDBOX_ARTEFACT_UNIT',
    'RESET_SANDBOX'
  ],
  namespacedPrefix
);

const {
  SANDBOX_DATA_REQUEST,
  SANDBOX_DATA_SUCCESS,
  SANDBOX_DATA_ERROR,
  SANDBOX_DATA_RESET,
  LOOKUP_SANDBOX_GUIDE,
  SET_SANDBOX_GUIDE,
  SET_SANDBOX_GUIDE_ON,
  LOOKUP_SANDBOX_TAGS,
  SET_SANDBOX_TAGS,
  LOOKUP_SANDBOX_ARTEFACTS,
  SET_SANDBOX_ARTEFACTS,
  LOOKUP_SANDBOX_ARTEFACT,
  SET_SANDBOX_ARTEFACT,
  LOOKUP_SANDBOX_ARTEFACT_UNIT,
  SET_SANDBOX_ARTEFACT_UNIT,
  RESET_SANDBOX
} = mutationTypes;

/**
 * Auth data mutations
 */
const mutations = {
  /** auth data request */
  [SANDBOX_DATA_REQUEST](state) {
    Object.assign(state, { loading: true, error: null });
  },

  /** auth data success */
  [SANDBOX_DATA_SUCCESS](state, payload) {
    Object.assign(state, { loading: false, data: payload, user: payload, isAuthenticated: true });
  },

  /** auth data error */
  [SANDBOX_DATA_ERROR](state, payload) {
    Object.assign(state, {
      loading: false,
      data: null,
      error: payload || true
    });
  },

  /** reset auth data */
  [SANDBOX_DATA_RESET](state) {
    Object.assign(state, initialState);
  },

  [LOOKUP_SANDBOX_GUIDE](state) {
    state.loading = true;
  },
  [SET_SANDBOX_GUIDE](state, payload) {
      state.guide = payload.res.data;
      state.loading = false;
  },
  [SET_SANDBOX_GUIDE_ON](state, guide) {
      state.guideOn = guide;
      state.loading = false;
  },
  [LOOKUP_SANDBOX_TAGS](state) {
      state.loading = true;
  },
  [SET_SANDBOX_TAGS](state, payload) {
      state.tags = payload.res.data;
      state.tagsCount = payload.res.data ? payload.res.data.length : 0;
      state.loading = false;
  },
  [LOOKUP_SANDBOX_ARTEFACTS](state) {
      state.loading = true;
  },
  [SET_SANDBOX_ARTEFACTS](state, payload) {
      state.artefacts = payload.artefacts;
      state.artefactsCount = payload.artefacts ? payload.artefacts.length : 0;
      state.loading = false;
  },
  [LOOKUP_SANDBOX_ARTEFACT](state) {
      state.loading = true;
  },
  [SET_SANDBOX_ARTEFACT](state, payload) {
    //   state.artefact = payload;
    //   state.artefact = Object.assign({}, state.artefact, payload)
    state.artefact = {};
    state.artefact = payload;
      state.loading = false;
  },
  [LOOKUP_SANDBOX_ARTEFACT_UNIT](state) {
      state.loading = true;
  },
  [SET_SANDBOX_ARTEFACT_UNIT](state, payload) {
    state.artefactUnit = payload;
    state.loading = false;
  },
  [RESET_SANDBOX](state) {
      for (let f in state) {
          Vue.set(state, f, state.initialState[f]);
      }
  }

};

/** Actions types constants */
export const actionsTypes = reflectKeys([
  'FETCH_SANDBOX_GUIDE',
  'FETCH_SANDBOX_TAGS',
  'FETCH_SANDBOX_ARTEFACTS',
  'FETCH_SANDBOX_ARTEFACT',
  'FETCH_SANDBOX_ARTEFACT_UNIT',
  'RESET_SANDBOX_STATE'
], namespacedPrefix);

/**
 * Users data actions
 */
const actions = {

  async [actionsTypes.FETCH_SANDBOX_GUIDE](context, payload) {
    context.commit(LOOKUP_SANDBOX_GUIDE);
    return await fetchGuide(payload)
        .then(data => {
            payload.res = data;
            context.commit(SET_SANDBOX_GUIDE, payload);
        })
        .catch(error => {
            payload.res = error;
            context.commit(SET_SANDBOX_GUIDE, payload);
        });
  },
  async [actionsTypes.FETCH_SANDBOX_TAGS]({ commit }, payload) {
      commit(LOOKUP_SANDBOX_TAGS);
      return await fetchTags(payload)
          .then(data => {
              payload.res = data;
              commit(SET_SANDBOX_TAGS, payload);
          })
          .catch(error => {
              payload.res = error;
              commit(SET_SANDBOX_TAGS, payload);
          });
  },
  async [actionsTypes.FETCH_SANDBOX_ARTEFACTS]({ commit }, payload) {
      commit(LOOKUP_SANDBOX_ARTEFACTS);
      return await fetchArtefacts(payload)
          .then(data => {
              payload.res = data;
              commit(SET_SANDBOX_ARTEFACTS, payload);
          })
          .catch(error => {
              payload.res = error;
              commit(SET_SANDBOX_ARTEFACTS, payload);
          });
  },
  async [actionsTypes.FETCH_SANDBOX_ARTEFACT](context, payload, prevArtifact) {
      // avoid extronuous network call if search exists
      context.commit(LOOKUP_SANDBOX_ARTEFACT);
      if (prevArtifact !== undefined) {
          return context.commit(SET_SANDBOX_ARTEFACT, prevArtifact);
      }
      if(payload.mock) {
        return await fetchMock(payload)
            .then(res => {
                if(res.data.data) {
                  payload.res = res.data.data;
                } else {
                  payload.res = res.data;
                }
                context.commit(SET_SANDBOX_ARTEFACT, payload);
            })
            .catch(error => {
                payload.res = {error: 'No records found for the search criteria.'};
                context.commit(SET_SANDBOX_ARTEFACT, payload);
                // throw new Error(error);
            });
      } else {
        return await fetchArtefact(payload)
            .then(data => {
                if(data.data.result) {
                  payload.res = data.data.result;
                } else {
                  payload.res = data.data;
                }
                context.commit(SET_SANDBOX_ARTEFACT, payload);
            })
            .catch(error => {
                payload.res = {error: 'No records found for the search criteria.'};
                context.commit(SET_SANDBOX_ARTEFACT, payload);
                // throw new Error(error);
            });
          }
  },
  async [actionsTypes.FETCH_SANDBOX_ARTEFACT_UNIT](context, payload, prevArtifact) {
      // avoid extronuous network call if search exists
      context.commit(LOOKUP_SANDBOX_ARTEFACT_UNIT);
      if (prevArtifact !== undefined) {
          return context.commit(SET_SANDBOX_ARTEFACT_UNIT, prevArtifact);
      }
      return await fetchArtefactUnit(payload)
          .then(data => {
              payload.res = data.data;
              context.commit(SET_SANDBOX_ARTEFACT_UNIT, payload);
          })
          .catch(error => {
            payload.res = {error: 'No records found for the given criteria.'};
              context.commit(SET_SANDBOX_ARTEFACT_UNIT, payload);
          });
  },
  [actionsTypes.RESET_SANDBOX_STATE]({ commit }) {
      commit(RESET_SANDBOX);
  } 
};

/**
 * Users data actions
 */
const state = {
  ...initialState,
  guideOn: [],
  tagsCount: 0,
  artefactsCount: 0
};

const getters = {
  isLoading( state ) {
    return state.loading;
  },
  tagsCount( state ) {
      return state.tagsCount;
  },
  tags( state ) {
      return state.tags;
  },
  artefactsCount( state ) {
      return state.artefactsCount;
  },
  artefacts( state ) {
      return state.artefacts;
  },
  artefact( state ) {
      return state.artefact;
  },
  artefactUnit( state ) {
      return state.artefactUnit;
  },
  guide( state ) {
      return state.guide;
  },
  guideOn( state ) {
      return state.guideOn;
  }
};

export default {
  mutations,
  actions,
  state,
  getters
};
